import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { VERSION } from 'utils/campaign';
import { useFlags } from 'launchdarkly-react-client-sdk';
import withSelectedCategoryId from 'HOC/withSelectedCategoryId/withSelectedCategoryId';
import withVersion from 'HOC/withVersion/withVersion';
import { setSelectedSort } from 'services/actions/sortActions';
import {
  requestProducts,
  requestFavorites,
} from 'services/actions/productsActions';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import { selectSponsoredProductsCategory } from 'selectors/categoriesSelectors';
import MobileMenu from 'shared-components/MobileMenu/MobileMenu';
import { track } from 'utils/segment';
import { sortIdToTrackingValue, getSortValues } from 'utils/sort';
import { CATEGORY_ID, ALL_CATEGORY } from 'utils/categories';
import CategoryHeader from '../CategoryHeader/CategoryHeader';
import styles from './SaleBar.module.css';

const SaleBarButton = ({ name, icon, onClick }) => (
  <button type="button" className={styles.button} onClick={onClick}>
    <i className={icon} />
    {name}
  </button>
);

SaleBarButton.propTypes = {
  name: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const STATIC_CATEGORIES = [
  ...ALL_CATEGORY,
  {
    id: 'mycart',
    name: 'My Cart',
    children: [],
  },
];

const SaleBar = ({
  selectedSort,
  categories,
  loggedIn,
  selectedCategoryId,
  sponsoredProductsCategory,
  version,
  trackingData,
  setSelectedSort,
  requestProducts,
  requestFavorites,
}) => {
  const {
    rateAndReview: rateAndReviewFF,
    alternateSubscriptions: alternateSubscriptionsFF,
    publicSubscriptions: publicSubscriptionsFF,
  } = useFlags();
  const headerElem = document.getElementById('global-nav');
  const history = useHistory();
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(headerElem.offsetHeight);
  const isFavoritesTab = selectedCategoryId === CATEGORY_ID.myfavorites;
  const sortOptions = getSortValues(loggedIn);
  const filterOptions = [
    ...(loggedIn ? STATIC_CATEGORIES : ALL_CATEGORY),
    ...sponsoredProductsCategory,
    ...categories,
  ];

  const toggleSortMenu = () => setSortMenuOpen((prevState) => !prevState);
  const toggleFilterMenu = () => setFilterMenuOpen((prevState) => !prevState);
  const handleSortTracking = (id) => {
    const data = {
      ...trackingData,
      experience: 'Sort By',
      sort_name: sortIdToTrackingValue[id],
    };
    track('Products Sorted', data);
  };
  const handleSort = (id) => {
    setSelectedSort(id);
    setSortMenuOpen(false);
    handleSortTracking(id);
  };
  const handleFilterTracking = (name) => {
    const page = version === VERSION.shop ? 'Shop' : 'Flash Sale';
    const filterName = name === 'All Products' ? 'All' : name;
    const data = {
      ...trackingData,
      experience: `${page} Filter`,
      filter_name: filterName,
    };
    track('Products Filtered', data);
  };
  const handleClickFavorites = () => {
    if (selectedCategoryId === 'myfavorites') {
      // refetch data
      requestFavorites();
      requestProducts({
        offset: 0,
        selectedCategoryId,
        selectedSort,
        selectedProductLink: null,
        rateAndReviewFF,
        alternateSubscriptionsFF,
        publicSubscriptionsFF,
      });
    } else {
      history.push('?category=myfavorites');
    }
    handleFilterTracking('My Favorites');
  };
  const handleFilter = (id, name) => {
    history.push({
      search: id ? `?category=${id}` : '',
    });
    setFilterMenuOpen(false);
    handleFilterTracking(name);
  };

  // update headerHeight state on window resize
  useEffect(() => {
    const handleWindowResize = () => {
      setHeaderHeight(headerElem.offsetHeight);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  return (
    <>
      <div
        className={classNames(styles.container, {
          //TO DO: update categories to all use the same header and styles
          [styles.favoritesTab]: isFavoritesTab,
        })}
        style={{ top: headerHeight }}
      >
        <SaleBarButton
          name="Sort"
          icon="fas fa-sort"
          onClick={toggleSortMenu}
        />
        {loggedIn && (
          <SaleBarButton
            name="My Favorites"
            icon="fas fa-heart"
            onClick={handleClickFavorites}
          />
        )}
        <SaleBarButton
          name="Filter"
          icon="fas fa-filter"
          onClick={toggleFilterMenu}
        />
      </div>
      {isFavoritesTab && (
        <CategoryHeader
          title="My Favorites"
          subtitle="All favorited items available in this sale will be found here. This includes anything you have favorited in the past."
        />
      )}
      <MobileMenu
        title="Sort By"
        options={sortOptions}
        selected={selectedSort}
        isOpen={sortMenuOpen}
        closeMenu={toggleSortMenu}
        onClick={handleSort}
        checkSelected
      />
      <MobileMenu
        title="Filter"
        options={filterOptions}
        selected={selectedCategoryId}
        isOpen={filterMenuOpen}
        closeMenu={toggleFilterMenu}
        onClick={handleFilter}
      />
    </>
  );
};

SaleBar.propTypes = {
  selectedSort: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedCategoryId: PropTypes.string,
  sponsoredProductsCategory: PropTypes.arrayOf(PropTypes.shape()),
  version: PropTypes.string.isRequired,
  trackingData: PropTypes.shape().isRequired,
  setSelectedSort: PropTypes.func.isRequired,
};

SaleBar.defaultProps = {
  selectedCategoryId: null,
  sponsoredProductsCategory: [],
};

const mapStateToProps = (state) => ({
  selectedSort: state.sortState.selectedSort,
  categories: state.categoriesState.categories,
  loggedIn: state.userState.loggedIn,
  sponsoredProductsCategory: selectSponsoredProductsCategory(state),
  trackingData: selectPageTrackingFullData(state),
});

const mapDispatchToProps = {
  setSelectedSort,
  requestProducts,
  requestFavorites,
};

export default compose(
  withSelectedCategoryId,
  withVersion,
  connect(mapStateToProps, mapDispatchToProps)
)(SaleBar);
